import { Component } from '@angular/core';

import { ProductApiService, IncomePriceBreakdownRow } from 'src/app/services/product-api.service';
import { round2 } from 'src/app/shared/round';

/** One depot's contribution to a multi-depot blended income price (see `openMultiDepot`) —
 *  `stock`/`price` are values the caller already computed (`PRODSTOCKM/I/A`/`PRODINPRICEM/I/A`),
 *  passed straight through rather than re-derived, so the summary math can never drift from
 *  what the host component's own formula produced. `rows` is filled in asynchronously, purely
 *  to explain *why* `price` is what it is — it plays no part in the blend arithmetic itself.
 *
 *  `stock`/`price` are `null` for a depot the caller has no comparably-computed number for —
 *  currently only the manufacturing/assembly site, added purely as an informational FIFO
 *  breakdown (see `EditDocumentContentComponent.openIncomePriceBreakdown`'s `mfg` depot): it is
 *  never part of `coveredQty`/`excessQty`/`stockPrice`/`fallbackPrice`, so there is no
 *  caller-computed number to pass through, only the per-batch `rows` the template renders. */
export interface DepotIncomePriceInput {
  label: string;
  stockId: number;
  stock: number | null;
  price: number | null;
}

interface DepotBreakdown extends DepotIncomePriceInput {
  rows: IncomePriceBreakdownRow[];
  loading: boolean;
  error: boolean;
}

/** Inputs for `openMultiDepot` — the invoice/proforma form's income price blends up to three
 *  depots plus a fallback for any quantity exceeding total stock (see
 *  `EditDocumentContentComponent.calculateDiRowProfitSync`). Every numeric field here is a
 *  value the host component already computed; this dialog only explains and re-displays it,
 *  never recomputes it independently. */
export interface IncomePriceBlendInput {
  prodId: number;
  cm: number;
  cr: number;
  qty: number;
  fd: Date;
  depots: DepotIncomePriceInput[];
  coveredQty: number;
  excessQty: number;
  stockPrice: number;
  fallbackPrice: number;
  cellPriceMain: number | null;
}

/**
 * Read-only popup showing the FIFO batch breakdown behind an income-price
 * cell — which source documents, at what price/quantity, were combined to
 * compute it. Design: `docs/PROMPT_income-price-breakdown-popup.md`.
 *
 * Self-contained "informational popup" pattern (local `visible` boolean,
 * `p-dialog [(visible)]`), same as `CompanyRegisterVerifyComponent` — not
 * the parent-controlled `@Input() display` pattern full create/edit forms
 * (e.g. `CreateNewDocumentComponent`) use.
 *
 * Opened imperatively via `open()`/`openMultiDepot()` from a parent grid
 * through a `@ViewChild` reference, rather than an `@Input`-driven two-way
 * binding: this dialog is reused from multiple different parent grids
 * (edit-document-content-receipt, edit-document-content) with no shared
 * parent state to bind through — a single imperative call from a row's
 * click handler is simpler than wiring `@Input`/`@Output` pairs in each
 * host for "show me this data now."
 *
 * Two modes, because the income price itself is computed two different
 * ways depending on the host:
 * - **`single`** (`open()`) — one FIFO call at one depot, as-of the
 *   document's own date. Used by `edit-document-content-receipt`, where
 *   `DOCINCOMEPRICE` genuinely is that one call's result (see the design
 *   doc's "Goal" section for why as-of-document-date, not live/now).
 * - **`multi`** (`openMultiDepot()`) — `edit-document-content` (invoice/
 *   proforma) blends up to three depots (Main/Import/Auxiliary) stock-
 *   weighted, plus a fallback for any quantity exceeding total stock,
 *   evaluated live (`new Date()`), not at the document's date. Wiring
 *   `single` mode there would show one depot's FIFO breakdown and
 *   routinely disagree with the cell for entirely legitimate structural
 *   reasons (multi-depot blending, the fallback branch) — not a real
 *   mismatch, but indistinguishable from one without this mode.
 */
@Component({
  selector: 'app-income-price-breakdown-dialog',
  templateUrl: './income-price-breakdown-dialog.component.html',
  styleUrls: ['./income-price-breakdown-dialog.component.scss'],
})
export class IncomePriceBreakdownDialogComponent {

  /** Mismatch threshold for all three currency comparisons below. Started at 0.01 (Chunk 3) but
   *  that flagged rounding noise as a real mismatch — e.g. 79 551,76 vs a recomputed 79 551,82,
   *  a ~0.00008% gap from the JS `docRateVal`-multiplication path vs SQL's own currency-rate
   *  conversion landing on a different rounding step, not a wrong price. Widened per owner
   *  request once that false positive was seen live. */
  private readonly MISMATCH_TOLERANCE = 0.5;

  visible = false;
  mode: 'single' | 'multi' = 'single';

  // ── single-depot mode state ──────────────────────────────────────────
  loading = false;
  error = false;
  rows: IncomePriceBreakdownRow[] = [];

  /** The `U4ETKA` value the clicked cell displayed — in the SAME currency as `cr`/`ConvertedPrice`
   *  below (the document's value currency, e.g. USD), so the mismatch check compares like with
   *  like. `null` until `open()` is called with one. Single mode only — `edit-document-content`'s
   *  `U4ETKA` is an editable sale price, not an income price, so `openMultiDepot` never sets this. */
  cellPriceCurrency: number | null = null;

  /** The `DOCITEMCRATE`/`DOCRATEVAL` used to convert `cellPriceCurrency` into `cellPriceMain` —
   *  needed to project the breakdown's (value-currency) weighted average into main currency for
   *  the second comparison, rather than re-deriving it from a separately-fetched rate. Single
   *  mode only. */
  docRateVal: number | null = null;

  /** The price actually RECORDED on this document line (`DOCITEMVPRICE`/`DOCITEMPRICE`) — distinct
   *  from `cellPriceCurrency`/`cellPriceMain` (`U4ETKA`/`DOCINCOMEPRICE`), which are recomputed
   *  *live* on every load (a fresh FIFO call for DOCTYPE 5, `prodInfo.PRODINPRICE` for DOCTYPE
   *  2/3 — see `EditDocumentContentReceiptComponent.calculateRowSummary`/`calculateDocItemsFields`)
   *  rather than read back from what the document itself recorded. `isStoredPriceMismatch` below
   *  compares this against `cellPriceCurrency` directly (NOT `weightedAvgPrice`, the popup's own
   *  breakdown-table recompute) — for DOCTYPE 5 the two are numerically the same call family so it
   *  makes no difference, but for DOCTYPE 2/3 `weightedAvgPrice` answers a different, unrelated
   *  question ("what would consuming this qty cost right now") and comparing against it would be
   *  meaningless; `cellPriceCurrency` is always the right "current known price" to compare against.
   *
   *  For a document type where the recorded price is supposed to equal the income price — DOCTYPE
   *  5 (an internal transfer carries cost, not a markup; `calculateDocItemsFields`'s DOCTYPE-5
   *  branch already computes `DOCITEMPROFIT = DOCITEMPRICE - DOCINCOMEPRICE` on that assumption)
   *  and DOCTYPE 2/3 (a receipt's price *is* the cost basis) — a drift here is a real bug the
   *  `cellPriceCurrency`/`cellPriceMain` comparison above cannot catch on its own, because that one
   *  only checks the popup's live recompute against the cell's own (also live) value; found live
   *  2026-09-03 on a DOCTYPE-5 transfer recorded at 647,99 against a re-priced FIFO average of
   *  826,50 once later receipts shifted the batch layers. `null` (the default) means the caller has
   *  no such recorded price to compare, or the comparison isn't meaningful for this document type.
   *  DOCTYPE 4 is the case that isn't meaningful: there is no stored/recorded income price there at
   *  all — `DOCITEMPRICE` is the sale price, and profit/margin are derived on-the-fly from that
   *  sale price against the live `DOCINCOMEPRICE`, never the other way around — so nothing
   *  analogous to `storedPriceCurrency` exists to pass for that DOCTYPE, and the popup's job there
   *  is exactly what `cellPriceCurrency`/`cellPriceMain` above already do: show where the live
   *  income-price value came from, nothing more (owner confirmed, 2026-09-03). Single mode only. */
  storedPriceCurrency: number | null = null;

  /** Main-currency counterpart of {@link storedPriceCurrency} (`DOCITEMPRICE`). Single mode only. */
  storedPriceMain: number | null = null;

  // ── multi-depot mode state ───────────────────────────────────────────
  depotBreakdowns: DepotBreakdown[] = [];
  blend: { coveredQty: number; excessQty: number; stockPrice: number; fallbackPrice: number; qty: number } | null = null;

  /** The `DOCINCOMEPRICE` value the clicked cell displayed — already in main currency (that
   *  field is documented "in main currency" on `DocItems`), passed straight through rather than
   *  recomputed so it can never drift from what the grid itself shows. `null` until `open()`/
   *  `openMultiDepot()` is called with one. Used by both modes. */
  cellPriceMain: number | null = null;

  constructor(private prodApi: ProductApiService) { }

  /**
   * Single-depot mode. Fetches and shows the breakdown for one income-price cell. Same five
   * positional parameters (`prodId, cm, s1, cr, qty, fd`) every existing `getProdIncomePriceById`
   * call site in this app already passes — copy the call, don't re-derive the params. `cr`
   * decides which currency `ConvertedPrice`/the reconstructed weighted average come back in —
   * pass the document's value currency (`DOCCURRID`) to match `cellPriceCurrency`, not the main
   * currency `cellPriceMain` is already expressed in.
   *
   * @param cellPriceCurrency the clicked cell's `U4ETKA` (value-currency) price — compared
   *   against the breakdown's own `ConvertedPrice`, which is returned in the SAME currency
   *   (`cr`) this call requested.
   * @param docRateVal the document's exchange rate — used only to project the reconstructed
   *   weighted average into main currency for the second comparison row.
   * @param cellPriceMain the clicked cell's `DOCINCOMEPRICE` (main-currency) price.
   * @param storedPriceCurrency the row's recorded `DOCITEMVPRICE`, when the caller's document
   *   type treats it as meant to equal the income price (see {@link storedPriceCurrency}'s own
   *   doc) — omit/pass `null` otherwise.
   * @param storedPriceMain the row's recorded `DOCITEMPRICE` (main currency); goes with
   *   `storedPriceCurrency`.
   */
  open(prodId: number, cm: number, s1: number, cr: number, qty: number, fd: Date,
       cellPriceCurrency: number | null, docRateVal: number | null, cellPriceMain: number | null,
       storedPriceCurrency: number | null = null, storedPriceMain: number | null = null): void {
    this.mode = 'single';
    this.visible = true;
    this.loading = true;
    this.error = false;
    this.rows = [];
    this.cellPriceCurrency = cellPriceCurrency;
    this.docRateVal = docRateVal;
    this.cellPriceMain = cellPriceMain;
    this.storedPriceCurrency = storedPriceCurrency;
    this.storedPriceMain = storedPriceMain;
    this.depotBreakdowns = [];
    this.blend = null;

    this.prodApi.getProdIncomePriceBreakdownById(prodId, cm, s1, cr, qty, fd).subscribe({
      next: (rows) => {
        this.rows = rows ?? [];
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      },
    });
  }

  /**
   * Multi-depot mode, for `edit-document-content` (invoice/proforma). All the arithmetic
   * (`coveredQty`/`excessQty`/`stockPrice`/`fallbackPrice`) is the host's own
   * `calculateDiRowProfitSync` values, passed straight through — this dialog only re-displays
   * that math for verification, never recomputes it, so it can't drift from the cell it was
   * opened from. The per-depot FIFO breakdowns (`depots[].stockId > 0` only — a `stockId` of 0
   * means that depot is disabled for this session, per `impCountFlag`/`auxCountFlag`, and
   * contributes nothing) load asynchronously underneath, purely to explain where each depot's
   * own `price` came from.
   */
  openMultiDepot(input: IncomePriceBlendInput): void {
    this.mode = 'multi';
    this.visible = true;
    this.loading = false;
    this.error = false;
    this.rows = [];
    this.cellPriceCurrency = null;
    this.docRateVal = null;
    this.storedPriceCurrency = null;
    this.storedPriceMain = null;
    this.cellPriceMain = input.cellPriceMain;
    this.blend = {
      coveredQty: input.coveredQty,
      excessQty: input.excessQty,
      stockPrice: input.stockPrice,
      fallbackPrice: input.fallbackPrice,
      qty: input.qty,
    };
    this.depotBreakdowns = input.depots
      .filter(d => d.stockId > 0)
      .map(d => ({ ...d, rows: [], loading: true, error: false }));

    this.depotBreakdowns.forEach(depot => {
      this.prodApi.getProdIncomePriceBreakdownById(input.prodId, input.cm, depot.stockId, input.cr, input.qty, input.fd).subscribe({
        next: (rows) => {
          depot.rows = rows ?? [];
          depot.loading = false;
        },
        error: () => {
          depot.error = true;
          depot.loading = false;
        },
      });
    });
  }

  onHide(): void {
    this.visible = false;
  }

  get isFallback(): boolean {
    return this.rows.length === 1 && !!this.rows[0]?.IsFallback;
  }

  get totalUnits(): number {
    return round2(this.rows.reduce((sum, r) => sum + (r.UnitsFromBatch ?? 0), 0));
  }

  get totalValue(): number {
    return round2(this.rows.reduce((sum, r) => sum + (r.LineValue ?? 0), 0));
  }

  /** Weighted-average price reconstructed from the batch rows, in the same (value) currency as
   *  `ConvertedPrice` — should match `cellPriceCurrency` (within rounding) when the cell it was
   *  opened from is correct. `null` when there are no rows at all (nothing to average, distinct
   *  from a genuine 0 price). Single mode only. */
  get weightedAvgPrice(): number | null {
    const units = this.totalUnits;
    return units > 0 ? round2(this.totalValue / units) : null;
  }

  /** `weightedAvgPrice` projected into main currency via `docRateVal`, for comparison against
   *  `cellPriceMain` — mirrors how every other main-currency field in this app is derived
   *  (`price * DOCRATEVAL`), never fetched as a second, potentially-inconsistent source. Single
   *  mode only. */
  get weightedAvgPriceMain(): number | null {
    return (this.weightedAvgPrice !== null && this.docRateVal !== null)
      ? round2(this.weightedAvgPrice * this.docRateVal)
      : null;
  }

  /** `true` when the reconstructed (value-currency) weighted average disagrees with the cell's
   *  own displayed `U4ETKA` price by more than rounding noise — the actual "check the
   *  correctness" signal this popup exists to surface. `false` while data is still
   *  loading/absent, so the mismatch styling never flashes on before the real numbers are in.
   *  Single mode only. */
  get isMismatch(): boolean {
    if (this.loading || this.error || this.weightedAvgPrice === null || this.cellPriceCurrency === null) {
      return false;
    }
    return Math.abs(this.weightedAvgPrice - this.cellPriceCurrency) > this.MISMATCH_TOLERANCE;
  }

  /** Same check as `isMismatch`, in main currency — kept separate rather than folded into one
   *  boolean because the two can legitimately disagree only due to rounding at a different
   *  decimal scale (main-currency amounts are ~40-50x larger), not because either comparison is
   *  wrong. Single mode only. */
  get isMismatchMain(): boolean {
    if (this.loading || this.error || this.weightedAvgPriceMain === null || this.cellPriceMain === null) {
      return false;
    }
    return Math.abs(this.weightedAvgPriceMain - this.cellPriceMain) > this.MISMATCH_TOLERANCE;
  }

  /** `true` when the price actually RECORDED on the document line (`storedPriceCurrency`)
   *  disagrees with the cell's own live-recomputed price (`cellPriceCurrency`) — this is the check
   *  that catches a line priced once at document-creation time that the live calculation has since
   *  moved past (see {@link storedPriceCurrency}'s doc for the live example, and for why this
   *  compares against `cellPriceCurrency` rather than `weightedAvgPrice`). Deliberately does NOT
   *  gate on `loading`/`error` — both operands are supplied synchronously by the caller, unrelated
   *  to the (possibly still-loading, possibly meaningless for this document type) breakdown-table
   *  fetch. `false` only when the caller didn't pass a stored price at all (nothing to compare).
   *  Single mode only. */
  get isStoredPriceMismatch(): boolean {
    if (this.cellPriceCurrency === null || this.storedPriceCurrency === null) {
      return false;
    }
    return Math.abs(this.cellPriceCurrency - this.storedPriceCurrency) > this.MISMATCH_TOLERANCE;
  }

  /** Same check as `isStoredPriceMismatch`, in main currency. Single mode only. */
  get isStoredPriceMismatchMain(): boolean {
    if (this.cellPriceMain === null || this.storedPriceMain === null) {
      return false;
    }
    return Math.abs(this.cellPriceMain - this.storedPriceMain) > this.MISMATCH_TOLERANCE;
  }

  /** Multi-mode's income sum: `coveredQty * stockPrice + excessQty * fallbackPrice` — the exact
   *  formula `calculateDiRowProfitSync` uses, over the same caller-supplied inputs. */
  get blendIncomeSum(): number | null {
    return this.blend
      ? round2((this.blend.coveredQty * this.blend.stockPrice) + (this.blend.excessQty * this.blend.fallbackPrice))
      : null;
  }

  /** `blendIncomeSum / qty` — should equal `cellPriceMain` exactly (both sides use the same
   *  inputs), so a mismatch here means the host's own values were stale relative to `qty` at
   *  the moment `openMultiDepot` was called (e.g. a qty edit that hadn't re-run
   *  `calculateDiRowProfitSync` yet), not a FIFO bug — this check exists to catch that timing
   *  gap, not to re-verify the arithmetic. */
  get blendComputedPrice(): number | null {
    return (this.blend && this.blend.qty > 0 && this.blendIncomeSum !== null)
      ? round2(this.blendIncomeSum / this.blend.qty)
      : null;
  }

  get isMismatchBlend(): boolean {
    if (this.blendComputedPrice === null || this.cellPriceMain === null) {
      return false;
    }
    return Math.abs(this.blendComputedPrice - this.cellPriceMain) > this.MISMATCH_TOLERANCE;
  }
}
